import { useState, useContext, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';

import DataContext from '../context';
import ReactTimetableContext from '../context/ReactTimetableContext';
import getSections from '../timetable/getSections';
import {
    getMatchingExclusion,
    getMatchingTutorials,
    hasMatchingRequirement,
    validateMatching,
} from '../utils/matchingUtils';
import Dialog from './Dialog';
import Presentation from './Presentation';

import styles from './SectionSelector.module.css';

const formatSectionTime = (datetime) => {
    if (!datetime || datetime.length === 0) return 'TBA';

    const timeStrings = [];

    for (const dtEntry of datetime) {
        if (!dtEntry || dtEntry === 'TBA') {
            timeStrings.push('TBA');
            continue;
        }

        if (dtEntry.match(/^[0-9]{2}-[A-Z]{3}-[0-9]{4}/i)) continue;

        const days = dtEntry.match(/(Mo|Tu|We|Th|Fr|Sa|Su)/gi);
        const times = dtEntry.match(/(0[1-9]|1[0-2]):[0-5][0-9](AM|PM)/gi);

        if (!days || !times || times.length < 2) {
            timeStrings.push(dtEntry);
            continue;
        }

        const daysStr = days.join(' ');
        const timeRange = `${times[0]} - ${times[1]}`;
        timeStrings.push(`${daysStr} ${timeRange}`);
    }

    return timeStrings.join(' / ') || 'TBA';
};

const SectionSelector = ({ courseCode }) => {
    const { data } = useContext(DataContext);
    const { addSection, removeSection } = useContext(ReactTimetableContext);

    const reduxSections = useSelector(state => state.app.timetable?.[courseCode] || []);

    const [isExpanded, setIsExpanded] = useState(true);
    const [exclusionNotice, setExclusionNotice] = useState(null);

    const activeSections = useMemo(() => {
        const active = {};
        reduxSections.forEach(section => {
            active[section] = true;
        });
        return active;
    }, [reduxSections]);

    const course = data?.[courseCode];
    const sections = useMemo(() => getSections(data, courseCode), [data, courseCode]);

    const matchingInfo = useMemo(() => {
        return hasMatchingRequirement(course);
    }, [course]);

    const visibleMatchingSections = useMemo(() => {
        const selectedLecture = reduxSections.find(section => /^L\d/i.test(section));
        if (!selectedLecture || !sections) return {};

        return Object.fromEntries(
            ['T', 'LA']
                .filter(sectionType => sections[sectionType])
                .map(sectionType => {
                    const matches = getMatchingTutorials(course, selectedLecture, sections[sectionType]);
                    return [sectionType, matches.length > 0 ? new Set(matches) : null];
                })
                .filter(([, matches]) => matches)
        );
    }, [course, reduxSections, sections]);

    const selectedLecture = reduxSections.find(section => /^L\d/i.test(section));

    useEffect(() => {
        setExclusionNotice(
            selectedLecture && course && sections
                ? getMatchingExclusion(course, selectedLecture, sections)
                : null
        );
    }, [course, sections, selectedLecture]);

    const sectionMap = useMemo(() => {
        if (!course?.sections) return {};

        const map = {};
        course.sections.forEach(sectionObj => {
            map[sectionObj.section] = sectionObj;
        });
        return map;
    }, [course]);

    const handleToggleExpand = useCallback(() => {
        setIsExpanded(prev => !prev);
    }, []);

    const handleSectionClick = useCallback((section, sectionType) => {
        if (!course || !sections || !sections[sectionType]) return;

        const isActive = activeSections[section];
        const isSingleton = sections[sectionType].length === 1;

        if (isActive && !isSingleton) {
            setExclusionNotice(null);
            removeSection(courseCode, section);
            return;
        }
        if (isActive) return;

        addSection(courseCode, section);
    }, [activeSections, courseCode, course, sections, addSection, removeSection]);

    const matchingValidation = useMemo(() => {
        const selectedSections = Object.keys(activeSections);
        return validateMatching(course, selectedSections);
    }, [course, activeSections]);

    if (!data || !course || !sections) return null;

    const credits = course.credit || 0;

    return (
        <div className={styles.sectionSelector}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <button
                        className={styles.expandButton}
                        onClick={handleToggleExpand}
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                        {isExpanded ? '▼' : '▶'}
                    </button>
                    <span className={styles.courseCode}>{courseCode}</span>
                    <span className={styles.courseName}>{course.name}</span>
                </div>
                <div className={styles.headerRight}>
                    <span className={styles.credits}>{credits} credits</span>
                </div>
            </div>

            {/* Matching requirement warning banner */}
            {matchingInfo && (
                <div className={clsx(
                    styles.matchingBanner,
                    !matchingValidation.isValid && styles.matchingError
                )}>
                    {!matchingValidation.isValid ? (
                        <>
                            <span className={styles.matchingIcon}>⚠️</span>
                            <span>{matchingValidation.message}</span>
                        </>
                    ) : (
                        <>
                            <span className={styles.matchingIcon}>ℹ️</span>
                            <span>Matching between Lecture &amp; Tutorial required. Changing one updates its pair.</span>
                        </>
                    )}
                </div>
            )}

            {isExpanded && (
                <div className={styles.sectionsContainer}>
                    {Object.keys(sections).map(sectionType => (
                        <div key={sectionType} className={styles.sectionTypeGroup}>
                            <h4 className={styles.sectionTypeLabel}>
                                {sectionType === 'L' ? 'Lecture' :
                                    sectionType === 'T' ? 'Tutorial' :
                                        sectionType === 'LA' ? 'Lab' : sectionType}
                                <span className={styles.sectionTypeHint}> (select one)</span>
                            </h4>
                            <div className={styles.sectionButtons}>
                                {sections[sectionType].filter(section => (
                                    !visibleMatchingSections[sectionType]
                                    || visibleMatchingSections[sectionType].has(section)
                                )).map(section => {
                                    const sectionObj = sectionMap[section];
                                    if (!sectionObj) return null;

                                    const isActive = activeSections[section];
                                    const timeStr = formatSectionTime(sectionObj.datetime);
                                    const isSingleton = sections[sectionType].length === 1;

                                    return (
                                        <button
                                            key={section}
                                            className={clsx(
                                                styles.sectionButton,
                                                isActive && styles.active,
                                                isSingleton && styles.singleton
                                            )}
                                            onClick={() => handleSectionClick(section, sectionType)}
                                            disabled={isSingleton && isActive}
                                        >
                                            <div className={styles.sectionLabel}>{section}</div>
                                            <div className={styles.sectionTime}>{timeStr}</div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {exclusionNotice && (
                <Presentation>
                    <Dialog
                        title="Matching sections filtered"
                        actions={[{
                            label: 'Got it',
                            onClick: () => setExclusionNotice(null),
                        }]}
                    >
                        <p>
                            <strong>{exclusionNotice.lecture}</strong> can only be combined with
                            {' '}{exclusionNotice.sections.join(' or ')}.
                        </p>
                        <p>
                            {exclusionNotice.hiddenSectionCount} non-matching section
                            {exclusionNotice.hiddenSectionCount === 1 ? ' was' : 's were'} hidden.
                        </p>
                    </Dialog>
                </Presentation>
            )}
        </div>
    );
};

export default SectionSelector;
